import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { updateOrder, closeSession, startSession } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatCurrency } from '../../utils/formatCurrency';
import TableOrderCard from './TableOrderCard';
import QuickInvoiceModal from './QuickInvoiceModal';
import WaiterOrderModal from './WaiterOrderModal';
import ConfirmActionModal from './ConfirmActionModal';
import { storage } from '../../utils/storage';
import './TableDetailPanel.css';

const PAY_ALL_CONFIG = {
  cash: { emoji: '💵', labelKey: 'waiter.payAllCash', defaultLabel: 'Cobrar todo efectivo', methodLabelKey: 'waiter.cash', defaultMethodLabel: 'efectivo' },
  card: { emoji: '💳', labelKey: 'waiter.payAllCard', defaultLabel: 'Cobrar todo tarjeta', methodLabelKey: 'waiter.card', defaultMethodLabel: 'tarjeta' },
  transfer: { emoji: '🏦', labelKey: 'waiter.payAllTransfer', defaultLabel: 'Cobrar todo transferencia', methodLabelKey: 'waiter.transfer', defaultMethodLabel: 'transferencia' },
  mercadopago: { emoji: '📱', labelKey: 'waiter.payAllMercadoPago', defaultLabel: 'Cobrar todo Mercado Pago', methodLabelKey: 'waiter.mercadopago', defaultMethodLabel: 'Mercado Pago' },
};

function TableDetailPanel({
  table,
  orders,
  isOpen,
  onClose,
  onStatusChange,
  onMarkPaid,
  onEditOrder,
  onCancelOrder,
  onTableAction,
  groupTables,
  paymentMethods,
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [closingTable, setClosingTable] = useState(false);
  const [payingAll, setPayingAll] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [startingSession, setStartingSession] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const activeSessionId = table?.currentSessionId || table?.sessionId || null;

  const tableOrders = useMemo(() => {
    if (!table) return [];
    // Filter by tableNumber to capture ALL orders for this table (including multiple sessions)
    return orders
      .filter((o) => String(o.tableNumber) === String(table.tableNumber))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [orders, table]);

  const totalAmount = tableOrders
    .filter((o) => o.status !== 'cancelled')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const paidAmount = tableOrders
    .filter((o) => o.paymentStatus === 'paid' && o.status !== 'cancelled')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const unpaidAmount = totalAmount - paidAmount;

  const unpaidOrders = tableOrders.filter(
    (o) => o.paymentStatus !== 'paid' && o.status !== 'cancelled'
  );

  const allPaid = tableOrders.length > 0 && unpaidAmount === 0;

  const paidNotInvoiced = tableOrders.filter(
    (o) => o.paymentStatus === 'paid' && !o.invoiceId && o.status !== 'cancelled'
  );
  const hasInvoiceableOrders = paidNotInvoiced.length > 0;

  // Calculate session duration
  const firstOrderTime = tableOrders
    .map((o) => new Date(o.createdAt).getTime())
    .sort((a, b) => a - b)[0];
  const sessionMinutes = firstOrderTime
    ? Math.floor((Date.now() - firstOrderTime) / 60000)
    : 0;

  // Derive enabled payment methods from restaurant config
  const enabledMethods = paymentMethods
    ? Object.entries(paymentMethods)
        .filter(([, config]) => config.enabled)
        .map(([key]) => key)
        .filter((key) => PAY_ALL_CONFIG[key])
    : ['cash', 'card'];

  const executePayAll = useCallback(async (method) => {
    setPayingAll(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await Promise.all(
        unpaidOrders.map((order) =>
          updateOrder(order.orderId, {
            restaurantId: order.restaurantId,
            date: order.PK?.split('#')[2] || today,
            paymentStatus: 'paid',
            paymentMethod: method,
          })
        )
      );
      onTableAction();
      showToast(t('toast.allOrdersPaid'));
    } catch (error) {
      console.error('Error paying all orders:', error);
      showToast(t('orders.updateError'), 'error');
    } finally {
      setPayingAll(false);
    }
  }, [unpaidOrders, onTableAction, showToast, t]);

  const handlePayAll = (method) => {
    if (unpaidOrders.length === 0) return;

    const payConfig = PAY_ALL_CONFIG[method];
    const methodLabel = payConfig ? t(payConfig.methodLabelKey, payConfig.defaultMethodLabel) : method;

    setConfirmAction({
      title: t('confirm.payAllTitle', 'Confirmar cobro'),
      message: t('confirm.payAllMessage', '¿Cobrar todos los pedidos pendientes?'),
      details: [
        { label: t('orders.total'), value: formatCurrency(unpaidAmount) },
        { label: t('confirm.orderCount', 'Pedidos'), value: String(unpaidOrders.length) },
        { label: t('confirm.paymentMethod', 'Medio de pago'), value: methodLabel },
      ],
      confirmLabel: `${payConfig.emoji} ${t(payConfig.labelKey, payConfig.defaultLabel)}`,
      variant: 'default',
      onConfirm: () => {
        setConfirmAction(null);
        executePayAll(method);
      },
    });
  };

  const executeCloseTable = useCallback(async (forceClose = false) => {
    setClosingTable(true);
    try {
      await closeSession(activeSessionId, forceClose);
      onTableAction();
      showToast(t('toast.tableClosed'));
      onClose();
    } catch (error) {
      console.error('Error closing table:', error);
      const data = error.response?.data;
      if (data?.unpaidCount && !forceClose) {
        setClosingTable(false);
        setConfirmAction({
          title: t('confirm.closeTableTitle', 'Cerrar mesa'),
          message: t('waiter.closeTableUnpaidConfirm', {
            count: data.unpaidCount,
            total: formatCurrency(data.unpaidTotal),
          }),
          details: [
            { label: t('confirm.unpaidOrders', 'Pedidos sin pagar'), value: String(data.unpaidCount) },
            { label: t('waiter.owes'), value: formatCurrency(data.unpaidTotal) },
          ],
          confirmLabel: t('confirm.forceClose', 'Forzar cierre'),
          variant: 'danger',
          onConfirm: () => {
            setConfirmAction(null);
            executeCloseTable(true);
          },
        });
        return;
      } else {
        showToast(t('waiter.closeTableError'), 'error');
      }
    } finally {
      setClosingTable(false);
    }
  }, [activeSessionId, onTableAction, onClose, showToast, t]);

  const handleCloseTable = () => {
    if (!activeSessionId) return;

    const details = [
      { label: t('orders.total'), value: formatCurrency(totalAmount) },
      { label: t('orders.paid'), value: formatCurrency(paidAmount) },
    ];

    if (unpaidAmount > 0) {
      details.push({ label: t('waiter.owes'), value: formatCurrency(unpaidAmount) });
      details.push({ label: t('confirm.unpaidOrders', 'Pedidos sin pagar'), value: String(unpaidOrders.length) });
    }

    if (unpaidAmount > 0) {
      setConfirmAction({
        title: t('confirm.closeTableTitle', 'Cerrar mesa'),
        message: t('confirm.closeTableUnpaidMessage', 'Esta mesa tiene pedidos sin pagar. ¿Cerrar de todos modos?'),
        details,
        confirmLabel: t('confirm.forceClose', 'Forzar cierre'),
        variant: 'danger',
        onConfirm: () => {
          setConfirmAction(null);
          executeCloseTable(true);
        },
      });
    } else {
      setConfirmAction({
        title: t('confirm.closeTableTitle', 'Cerrar mesa'),
        message: t('confirm.closeTableMessage', '¿Cerrar y liberar esta mesa?'),
        details,
        confirmLabel: t('waiter.closeAndFreeTable'),
        variant: 'default',
        onConfirm: () => {
          setConfirmAction(null);
          executeCloseTable(false);
        },
      });
    }
  };

  const restaurantId = table?.restaurantId || tableOrders[0]?.restaurantId || storage.get('selectedRestaurantId');

  const handleAddOrder = async () => {
    if (!activeSessionId) {
      const confirmed = window.confirm(t('waiterOrder.noSessionPrompt'));
      if (!confirmed) return;
      setStartingSession(true);
      try {
        await startSession(restaurantId, table.tableNumber);
        onTableAction();
        showToast(t('toast.sessionStarted'));
      } catch (err) {
        console.error('Error starting session:', err);
        showToast(t('waiterOrder.startSessionError'), 'error');
        setStartingSession(false);
        return;
      }
      setStartingSession(false);
    }
    setShowOrderModal(true);
  };

  const panelRef = useRef(null);

  // Escape key handler — skip if a child modal is open (WaiterOrderModal handles its own Escape)
  useEffect(() => {
    if (!isOpen || showOrderModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showOrderModal]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const panel = panelRef.current;
    const previouslyFocused = document.activeElement;

    // Focus the panel itself on open
    panel.focus();

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    panel.addEventListener('keydown', handleTab);
    return () => {
      panel.removeEventListener('keydown', handleTab);
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen || !table) return null;

  return (
    <>
      <div className="detail-panel-overlay" onClick={onClose} />
      <div
        className="detail-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-panel-title"
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="detail-panel-header">
          <div className="detail-panel-title">
            <h2 id="detail-panel-title">
              {t('orders.table')} {table.tableNumber}
              {groupTables?.length > 1 && (
                <span className="detail-panel-group-info">
                  {' '}+ {groupTables.filter((tn) => String(tn) !== String(table.tableNumber)).join(', ')}
                </span>
              )}
            </h2>
            {sessionMinutes > 0 && (
              <span className="detail-panel-duration">
                {t('orders.minutes', { count: sessionMinutes })}
              </span>
            )}
          </div>
          <button className="detail-panel-close" onClick={onClose}>✕</button>
        </div>

        <div className="detail-panel-totals">
          <div className="detail-total-item">
            <span className="detail-total-label">{t('orders.total')}</span>
            <span className="detail-total-value">
              {formatCurrency(totalAmount)}
            </span>
          </div>
          <div className="detail-total-item">
            <span className="detail-total-label">{t('orders.paid')}</span>
            <span className="detail-total-value detail-total-paid">
              {formatCurrency(paidAmount)}
            </span>
          </div>
          <div className="detail-total-item">
            <span className="detail-total-label">{t('waiter.owes')}</span>
            <span className="detail-total-value detail-total-unpaid">
              {formatCurrency(unpaidAmount)}
            </span>
          </div>
        </div>

        <div className="detail-panel-orders">
          <h3 className="detail-panel-section-title">{t('orders.title')}</h3>
          {tableOrders.length === 0 ? (
            <p className="detail-panel-empty">{t('orders.noOrders')}</p>
          ) : (
            tableOrders.map((order) => (
              <TableOrderCard
                key={order.orderId}
                order={order}
                onStatusChange={onStatusChange}
                onMarkPaid={onMarkPaid}
                onEditOrder={onEditOrder}
                onCancelOrder={onCancelOrder}
                paymentMethods={paymentMethods}
              />
            ))
          )}
        </div>

        <div className="detail-panel-actions">
          <button
            className="detail-action-btn add-order-btn"
            onClick={handleAddOrder}
            disabled={startingSession}
          >
            📝 {startingSession ? t('common.loading') : t('waiterOrder.addOrder')}
          </button>
          {unpaidAmount > 0 && (
            <div className="detail-panel-pay-actions">
              {enabledMethods.map((method) => {
                const config = PAY_ALL_CONFIG[method];
                return (
                  <button
                    key={method}
                    className={`detail-action-btn pay-${method}-btn`}
                    onClick={() => handlePayAll(method)}
                    disabled={payingAll}
                  >
                    {config.emoji} {t(config.labelKey, config.defaultLabel)}
                  </button>
                );
              })}
            </div>
          )}
          {hasInvoiceableOrders && (
            <button
              className="detail-action-btn invoice-btn"
              onClick={() => setShowInvoiceModal(true)}
            >
              🧾 {t('invoicing.emit', 'Facturar')}
            </button>
          )}
          {allPaid && activeSessionId && (
            <div className="detail-panel-close-section">
              <div className="detail-panel-paid-banner">
                {t('waiter.allPaidMessage')}
              </div>
              <button
                className="detail-action-btn close-table-btn close-table-btn--ready"
                onClick={handleCloseTable}
                disabled={closingTable}
              >
                {closingTable ? t('common.loading') : t('waiter.closeAndFreeTable')}
              </button>
            </div>
          )}
          {!allPaid && activeSessionId && (
            <button
              className="detail-action-btn close-table-btn"
              onClick={handleCloseTable}
              disabled={closingTable}
            >
              {closingTable ? t('common.loading') : t('waiter.closeTable')}
            </button>
          )}
        </div>

        {showInvoiceModal && (
          <QuickInvoiceModal
            orders={tableOrders}
            restaurantId={table.restaurantId || tableOrders[0]?.restaurantId}
            onClose={() => setShowInvoiceModal(false)}
            onCreated={() => {
              setShowInvoiceModal(false);
              onTableAction();
            }}
          />
        )}
      </div>

      {showOrderModal && (
        <WaiterOrderModal
          restaurantId={restaurantId}
          tableNumber={table.tableNumber}
          sessionId={activeSessionId}
          onClose={() => setShowOrderModal(false)}
          onOrderCreated={() => {
            setShowOrderModal(false);
            onTableAction();
          }}
        />
      )}

      {confirmAction && (
        <ConfirmActionModal
          title={confirmAction.title}
          message={confirmAction.message}
          details={confirmAction.details}
          confirmLabel={confirmAction.confirmLabel}
          variant={confirmAction.variant}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}

export default TableDetailPanel;
